import React, { useContext, type JSX } from 'react';
import { IUIUrval, TEDUrvalType } from '../../Types/UrvalTypes';
import { IColumnDef } from '../../Types/ColumnDefType';
import { convertUIUrvalToTEDUrval } from '../Urval/Utils/convertUIUrvalToTEDUrval';
import { getServerURL } from '../../Utils/getServerURL';
import Logger, { namedError } from '../../Utils/Logger';
import { IClientBusinessModel } from '../../Types/ClientModelTypes';
import { ExcelQuery } from '../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/Excel/Queries/ExcelQuery';
import { getCollectionInfo } from '../../Model/avenyModelUtils';
import { ModelContext } from '../../Model/ModelContext/ModelContext';
import { ISortProperty } from '../../BaseComponents/Table/Components/SortableTableUtils';
import { ActiveDatabaseContext } from '../ActiveDatabaseContext/ActiveDatabaseContext';
import { RutinSettingsContext } from '../../Features/Delsystem/_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import { rutiner } from '../../Features/Delsystem/_Common/Const/Rutiner';
import { ICollectionDataType } from '../../Features/Delsystem/_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { createIdsUrval } from '../Urval/Utils/urvalUtils';

interface IProps {
  collectionName: string;
  urval: IUIUrval | null;
  columns: readonly IColumnDef[];
  children: (exportToExcel: () => void) => JSX.Element;
  baseCollection?: IClientBusinessModel;
  ownId?: string;
  sortProperties: ISortProperty[];
  searchText?: string;
  rows?: ICollectionDataType[];
}

export const useExportToExcel = (props: Omit<IProps, 'children'>) => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const modelContext = useContext(ModelContext);
  const rutinSettingsContext = useContext(RutinSettingsContext);

  const exportToExcel = (dbName: string | null) => () => {
    if (!dbName) {
      Logger.error(
        namedError(
          'ExcelNoDB',
          'ExportToExcel kan inte användas utan en aktiv databas'
        ),
        { id: '5c740f12-064a-455b-a8d8-4b01a734527c' }
      );
    } else {
      const cols = props.columns
        .map(
          col =>
            JSON.stringify(col.attributeDef) +
            ':' +
            JSON.stringify(col.caption || '')
        )
        .join(',');

      let idsUrval: TEDUrvalType | undefined;
      if ((props.searchText || !props.urval) && props.rows?.length) {
        idsUrval = createIdsUrval(props.rows.map(row => row.id));
      }

      const data = {
        db: dbName,
        collectionName: props.collectionName,
        routine: rutinSettingsContext.activeRoutine?.routine.rutinID,
        fields: `{${cols}}`,
        selection: idsUrval
          ? JSON.stringify(idsUrval)
          : props.urval
          ? JSON.stringify(
              convertUIUrvalToTEDUrval(
                props.urval,
                props.baseCollection,
                props.ownId
              )
            )
          : undefined,
        selection_id:
          !props.urval && !idsUrval
            ? rutiner[
                rutinSettingsContext.activeRoutine?.routine.rutinKod || ''
              ]?.initialUrval
            : undefined,
        sortProperties: props.sortProperties,
      };

      const collectionInfo = getCollectionInfo(
        props.collectionName,
        null,
        modelContext.avenyCollectionMap
      );

      ExcelQuery(
        data,
        collectionInfo.collection.RootType.POLabel,
        getServerURL(),
        'Generic',
        loading => {}
      );
    }
  };

  return exportToExcel(activeDatabaseContext.activeDatabase);
};

const ExportToExcel: React.FC<React.PropsWithChildren<IProps>> = props => {
  const exportToExcel = useExportToExcel(props);

  return props.children(exportToExcel);
};

export default ExportToExcel;
